import { useCallback } from 'react';

import { createActionsHook, createHook } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	PROMPT_TILES_FINAL_RESPONSE_STREAM_MESSAGE_TYPE,
	type PromptTilesStreamMessage,
	type StreamError,
	SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE,
	type SuggestedPromptTile,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInjectedParams } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { FetchError } from '@atlassian/conversation-assistant-utils/httpUtils';

import { getPrimaryPrompts } from './getPrimaryPrompts';
import { getSecondaryPrompts } from './getSecondaryPrompts';
import { PRIMARY_PROMPTS_COUNT } from './PRIMARY_PROMPTS_COUNT';
import { PROMPT_SUGGESTIONS_OFFSET } from './PROMPT_SUGGESTIONS_OFFSET';
import { PromptSuggestionsEmptyError } from './PromptSuggestionsEmptyError';
import { SECONDARY_PROMPTS_COUNT } from './SECONDARY_PROMPTS_COUNT';
import { store } from './store';
import type { UsePromptSuggestionsReturn } from './types';

const TOTAL_PROMPT_SUGGESTIONS_COUNT = PRIMARY_PROMPTS_COUNT + SECONDARY_PROMPTS_COUNT;

const STREAM_ERROR_MESSAGE_TYPE: StreamError['type'] = 'ERROR';

type ParsedPromptSuggestionsStream = {
	tiles: SuggestedPromptTile[];
	/** Message types that weren't tiles, `ERROR` or `FINAL_RESPONSE`. */
	skippedMessageTypes: string[];
	/** The backend's `ERROR` message, if it sent one (it does when the request fails partway through). */
	streamError?: StreamError['message'];
	/** Number of tiles received before the `ERROR` message. */
	tilesBeforeStreamError?: number;
	/** `tiles_sent` from the `FINAL_RESPONSE` message, if one was received. */
	tilesSent?: number;
};

/**
 * Builds the error for a backend `ERROR` message. The message is fixed so Sentry groups these
 * together; the details go in `errorAttributes`, which the experience tracker forwards to Sentry
 * and analytics.
 */
const createStreamError = (streamError: StreamError['message']): FetchError =>
	new FetchError('Prompt suggestions stream returned an error', {
		statusCode: streamError.status_code,
		errorCode: streamError.message_template,
	});

const parsePromptSuggestionsStream = async ({
	generator,
	maxTiles,
	onPrimaryPromptsReady,
	onSecondaryPromptsReady,
}: {
	generator: AsyncGenerator<PromptTilesStreamMessage>;
	/**
	 * Upper bound on tiles to read. Exceeds the requested `limit` when the backend adds
	 * the 3P tile on top of the prompts, so stopping at `limit` would drop it whenever it
	 * streams last.
	 */
	maxTiles: number;
	onPrimaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
	onSecondaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
}): Promise<ParsedPromptSuggestionsStream> => {
	const tiles: SuggestedPromptTile[] = [];
	// Unexpected message types are ignored for rendering, but we keep them so an empty result
	// can tell "backend sent nothing" apart from "backend sent something we don't recognise".
	const skippedMessageTypes = new Set<string>();
	let streamError: StreamError['message'] | undefined;
	let tilesBeforeStreamError: number | undefined;
	let tilesSent: number | undefined;

	try {
		for await (const chunk of generator) {
			if (chunk.type === STREAM_ERROR_MESSAGE_TYPE) {
				streamError = chunk.message;
				tilesBeforeStreamError = tiles.length;
				continue;
			}
			if (chunk.type === PROMPT_TILES_FINAL_RESPONSE_STREAM_MESSAGE_TYPE) {
				tilesSent = chunk.message.tiles_sent;
				continue;
			}
			if (chunk.type !== SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE) {
				// The type says this can't happen, but the backend may add new message types.
				skippedMessageTypes.add((chunk as { type: string }).type);
				continue;
			}

			tiles.push(chunk.message);

			if (tiles.length === PRIMARY_PROMPTS_COUNT) {
				onPrimaryPromptsReady(getPrimaryPrompts(tiles));
			}

			if (tiles.length === maxTiles) {
				onSecondaryPromptsReady(getSecondaryPrompts(tiles));
				break;
			}
		}
	} catch (error) {
		if (tiles.length === 0) {
			throw error;
		}
	} finally {
		// If stream returns < maxTiles (on either success or partial failure), return whatever we have
		if (tiles.length < PRIMARY_PROMPTS_COUNT) {
			onPrimaryPromptsReady(getPrimaryPrompts(tiles));
		}
		if (tiles.length < maxTiles) {
			onSecondaryPromptsReady(getSecondaryPrompts(tiles));
		}
	}

	return {
		tiles: tiles.slice(0, maxTiles),
		skippedMessageTypes: Array.from(skippedMessageTypes),
		streamError,
		tilesBeforeStreamError,
		tilesSent,
	};
};

const useStore = createHook(store);

const useStoreActions = createActionsHook(store);

type PromptSuggestionsStateOptions = AssistanceServiceInjectedParams & {
	thirdPartyPrompt: { enable3pPrompt: boolean; ready: boolean };
};

export const usePromptSuggestionsState = ({
	assistanceServiceInjectedParams,
	thirdPartyPrompt: { enable3pPrompt, ready: is3pReady },
}: PromptSuggestionsStateOptions): UsePromptSuggestionsReturn => {
	const { assistanceService } = assistanceServiceInjectedParams;
	const [state] = useStore();
	const {
		getState,
		setLoading,
		setPrimaryPrompts,
		setSecondaryPrompts,
		setData,
		shuffleSuggestions,
		setError,
	} = useStoreActions();

	const loadSuggestions = useCallback(async () => {
		// Wait for the 3P signal to settle so we request a stable flag value (always
		// ready off a 3P surface); the host effect re-fires this when `is3pReady` flips.
		if (!is3pReady) {
			return;
		}

		const { isLoading, data, error, primaryPrompts, secondaryPrompts } = getState();
		if (isLoading || data || error || primaryPrompts || secondaryPrompts) {
			return;
		}

		setLoading();

		try {
			const { generator } = await assistanceService.fetchPersonalizedPromptSuggestionsStream({
				offset: PROMPT_SUGGESTIONS_OFFSET,
				limit: TOTAL_PROMPT_SUGGESTIONS_COUNT,
				enable3pPrompt,
			});

			const {
				tiles: prompts,
				skippedMessageTypes,
				streamError,
				tilesBeforeStreamError,
				tilesSent,
			} = await parsePromptSuggestionsStream({
				generator,
				// The 3P tile comes on top of the `limit` prompts rather than inside them, so
				// the variants that asked for it must read one tile more than they requested.
				maxTiles: TOTAL_PROMPT_SUGGESTIONS_COUNT + (enable3pPrompt ? 1 : 0),
				onPrimaryPromptsReady: setPrimaryPrompts,
				onSecondaryPromptsReady: setSecondaryPrompts,
			});

			if (prompts.length === 0) {
				if (fg('rovo_chat_empty_prompt_tiles_fix')) {
					// A backend ERROR message means the request failed partway through, so it must
					// still count as a failure.
					if (streamError) {
						throw createStreamError(streamError);
					}
					// Only treat it as empty when the backend confirms it sent no tiles. That's an
					// expected outcome (e.g. the user has no recent worked-on activity to personalise
					// from). Keep surfacing it as `error` so hosts still fall back to static prompts,
					// but type it so the loading experience doesn't count it as a failure.
					if (tilesSent === 0) {
						throw new PromptSuggestionsEmptyError({ skippedMessageTypes });
					}
				}
				// Otherwise (no FINAL_RESPONSE, or it reports tiles we never received) keep
				// treating it as a failure.
				throw new Error('No prompt suggestions returned');
			}

			if (streamError && fg('rovo_chat_empty_prompt_tiles_fix')) {
				// The backend failed after sending some tiles. We still show the partial tiles,
				// but report the error so it isn't lost.
				void logException({
					exception: createStreamError(streamError),
					name: 'PromptSuggestionsPartialStreamError',
					tags: {
						...(tilesBeforeStreamError !== undefined && {
							tilesReceived: String(tilesBeforeStreamError),
						}),
						...(streamError.status_code !== undefined && {
							statusCode: String(streamError.status_code),
						}),
						...(streamError.message_template && {
							messageTemplate: streamError.message_template,
						}),
					},
				});
			}
			setData(prompts);
		} catch (err: unknown) {
			setError(err instanceof Error ? err : new Error('Failed to fetch prompt suggestions'));
		}
	}, [
		assistanceService,
		getState,
		setLoading,
		setPrimaryPrompts,
		setSecondaryPrompts,
		setData,
		setError,
		is3pReady,
		enable3pPrompt,
	]);

	return {
		...state,
		// Surfaced so the grid can hold the 3P tile's slot while it streams.
		enable3pPrompt,
		loadSuggestions,
		shuffleSuggestions,
	};
};
